import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./PreBookingForm.css";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// 🔥 FIXED: Changed "iPhone 18 Duo" to "iPhone Duo" to match your DB
const IPHONE_MODELS = ["iPhone 18 Pro", "iPhone 18 Pro Max", "iPhone Duo"]; 
const ADVANCE_AMOUNT = 10000; // Fixed Advance Payment

const initialForm = {
  fullName: "",
  email: "",  
  phone: "",
  gstNumber: "",
  productId: "",
  model: "",
  storage: "256GB",
  color: "Titanium Black",
  quantity: 1,
  paymentMethod: "deposit",
  notes: "",
};

async function fetchProducts() {
  const res = await fetch(`${API}/products`);
  if (!res.ok) throw new Error("Failed to load products");
  return res.json();
}

async function createRazorpayOrder(amount) {
  const res = await fetch(`${API}/bookings/create-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount }),
  });
  if (!res.ok) throw new Error("Failed to initiate payment");
  return res.json();
}

async function verifyPayment(paymentData) {
  const res = await fetch(`${API}/bookings/verify-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(paymentData),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Payment verification failed");
  }
  return res.json();
}

function SuccessScreen({ booking, onReset }) {
  if (!booking) return null;
  return (
    <div className="success-screen">
      <div className="check">✓</div>
      <h1>Pre-Booking Confirmed!</h1>
      <p>Your reservation for the <strong>{booking.model}</strong> is in.</p>
      <div className="ref-box">
        <span>Booking Reference</span>
        <strong>{booking.bookingRef}</strong>
      </div>
      <ul className="success-details">
        <li><span>Storage</span><strong>{booking.storage}</strong></li>
        <li><span>Color</span><strong>{booking.color}</strong></li>
        <li><span>Amount Paid</span><strong>₹{booking.depositAmount}</strong></li>
        <li><span>Status</span><strong className={`badge ${booking.status}`}>{booking.status}</strong></li>
      </ul>
      <button className="btn-primary" onClick={onReset}>Book another iPhone</button>
    </div>
  );
}

export default function PreBookingForm({ onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [fetchError, setFetchError] = useState("");
  
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  useEffect(() => {
    fetchProducts()
      .then((all) => {
        // This filter now catches "iPhone Duo" correctly
        const filtered = all.filter((p) => IPHONE_MODELS.includes(p.name));
        setProducts(filtered);
        
        if (filtered.length > 0) {
          setForm((f) => ({
            ...f,
            productId: filtered[0]._id,
            model: filtered[0].name,
            storage: filtered[0].storage?.[0] || "256GB",
            color: filtered[0].colors?.[0]?.name || "Titanium Black",
          }));
        } else {
          setFetchError("No iPhone 18 models found in database.");
        }
      })
      .catch((err) => setFetchError("Failed to connect to the server."))
      .finally(() => setLoadingProducts(false));
  }, []);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const selectProduct = (p) => {
    setForm((f) => ({
      ...f,
      productId: p._id,
      model: p.name,
      storage: p.storage?.[0] || "256GB",
      color: p.colors?.[0]?.name || "Titanium Black",
    }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 1 && !form.productId) e.product = "Please select an iPhone model";
    if (step === 2) {
      if (!form.fullName.trim()) e.fullName = "Full name is required";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
      if (!/^[\d+\s()-]{7,}$/.test(form.phone)) e.phone = "Valid phone required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  // Gets the price of a specific product based on current storage/color
  const getProductPrice = (product) => {
    if (!product) return 0;
    if (product.variants && product.variants.length > 0) {
      const exactMatch = product.variants.find(
        (v) => v.storage === form.storage && v.color === form.color
      );
      if (exactMatch && exactMatch.price) return exactMatch.price;

      const storageMatch = product.variants.find((v) => v.storage === form.storage);
      if (storageMatch && storageMatch.price) return storageMatch.price;

      return product.variants[0].price;
    }
    return product.price || 0;
  };

  const selectedProduct = products.find((p) => p._id === form.productId);
  const currentPrice = getProductPrice(selectedProduct);

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setErrors({});

    try {
      const order = await createRazorpayOrder(ADVANCE_AMOUNT);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        name: "Crescent World",
        description: `Pre-Booking Advance: ${form.model}`,
        order_id: order.id,
        handler: async function (response) {
          try {
            const data = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              formData: { ...form, depositAmount: ADVANCE_AMOUNT },
            });

            setConfirmed(data.booking);
            onSuccess?.(data.booking);
          } catch (err) {
            setErrors({ submit: err.message });
          } finally {
            setSubmitting(false);
          }
        },
        prefill: { name: form.fullName, email: form.email, contact: form.phone },
        theme: { color: "#000" },
        modal: {
          ondismiss: function () {
            setSubmitting(false);
            setErrors({ submit: "Payment cancelled by user" });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setErrors({ submit: err.message });
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmed(null);
    setForm(initialForm);
    setStep(1);
    setErrors({});
  };

  return (
    <div>
      <Header />
      <div className="iphone-booking-wrapper my-5">
        <div className="iphone-booking">
          {confirmed ? (
            <SuccessScreen booking={confirmed} onReset={reset} />
          ) : (
            <>
              {step === 1 && (
                <section>
                  <h2>Choose your 18 Series</h2>
                  
                  {loadingProducts ? (
                    <div className="text-center py-5"><h3>Loading Products...</h3></div>
                  ) : fetchError ? (
                    <div className="alert alert-danger">{fetchError}</div>
                  ) : (
                    <div className="showcase-grid">
                      {products.map((p) => {
                        const cardPrice = getProductPrice(p);
                        
                        return (
                          <div
                            key={p._id}
                            className={`model-card ${form.productId === p._id ? "active" : ""}`}
                            onClick={() => selectProduct(p)}
                          >
                            {p.images && p.images.length > 0 ? (
                              <img 
                                className="phone-img" 
                                src={p.images[0]} 
                                alt={p.name} 
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}

                            <div 
                              className="phone-mock" 
                              style={{ display: p.images && p.images.length > 0 ? 'none' : 'block' }}
                            >
                              <div className="notch" />
                              <div className="phone-screen" />
                            </div>

                            <h3>{p.name}</h3>
                            
                            <div className="price-row">
                              <span className="pre-price">₹ {cardPrice.toLocaleString()}</span>
                            </div>
                            
                            <button type="button" className="select-btn">
                              {form.productId === p._id ? "✓ Selected" : "Select"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {products.length > 0 && (
                    <>
                      <div className="config-row">
                        <label>Storage</label>
                        <div className="chip-group">
                          {(selectedProduct?.storage || ["128GB", "256GB", "512GB", "1TB"]).map((s) => (
                            <button
                              key={s}
                              type="button"
                              className={`chip ${form.storage === s ? "active" : ""}`}
                              onClick={() => update("storage", s)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="config-row">
                        <label>Color</label>
                        <div className="chip-group">
                          {(selectedProduct?.colors || []).map((c) => (
                            <button
                              key={c.name}
                              type="button"
                              className={`color-chip ${form.color === c.name ? "active" : ""}`}
                              onClick={() => update("color", c.name)}
                              title={c.name}
                            >
                              <span style={{ background: c.hex }} />
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '20px', textAlign: 'right', fontSize: '18px' }}>
                        Total Price: <strong>₹ {currentPrice.toLocaleString()}</strong>
                      </div>
                    </>
                  )}
                </section>
              )}

              {step === 2 && (
                <section>
                  <h2>Your details</h2>
                  <div className="grid-2">
                    <div className="field">
                      <label>Full Name *</label>
                      <input value={form.fullName} onChange={(e) => update("fullName", e.target.value)} placeholder="Jane Doe" />
                      {errors.fullName && <span className="err">{errors.fullName}</span>}
                    </div>
                    <div className="field">
                      <label>Email *</label>
                      <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@example.com" />
                      {errors.email && <span className="err">{errors.email}</span>}
                    </div>
                    <div className="field">
                      <label>Phone *</label>
                      <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 98765 43210" />
                      {errors.phone && <span className="err">{errors.phone}</span>}
                    </div>
                    <div className="field">
                      <label>GST Number (Optional)</label>
                      <input value={form.gstNumber} onChange={(e) => update("gstNumber", e.target.value)} placeholder="GST Number" />
                    </div>
                  </div>
                  <div className="field">
                    <label>Address</label>
                    <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Delivery Address" />
                  </div>
                </section>
              )}

              {step === 3 && (
                <section>
                  <h2>Review & Pay</h2>
                  <div className="review-card">
                    <div className="review-row"><span>Model</span><strong>{form.model}</strong></div>
                    <div className="review-row"><span>Storage</span><strong>{form.storage}</strong></div>
                    <div className="review-row"><span>Color</span><strong>{form.color}</strong></div>
                    <div className="review-row"><span>Total Price</span><strong>₹ {currentPrice.toLocaleString()}</strong></div>
                    <div className="review-row" style={{ borderTop: "2px solid #000", paddingTop: "16px", marginTop: "16px" }}>
                      <span>Advance Payment</span>
                      <strong style={{ color: "#000", fontSize: "24px" }}>₹ {ADVANCE_AMOUNT.toLocaleString()}</strong>
                    </div>
                  </div>
                  {errors.submit && <p className="err">{errors.submit}</p>}
                </section>
              )}

              {products.length > 0 && (
                <div className="nav-buttons">
                  {step > 1 && (
                    <button type="button" className="btn-secondary" onClick={back} disabled={submitting}>
                      ← Back
                    </button>
                  )}
                  {step < 3 ? (
                    <button type="button" className="btn-primary" onClick={next}>
                      Continue →
                    </button>
                  ) : (
                    <button type="button" className="btn-primary" onClick={submit} disabled={submitting}>
                      {submitting ? "Processing..." : `Pay ₹${ADVANCE_AMOUNT.toLocaleString()} & Confirm`}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}